import { useEffect, useMemo, useState } from "react";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  isSameDay,
  isSameMonth,
  isValid,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { it } from "date-fns/locale";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, Clock3 } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";

const ICONS = { time: Clock3, "datetime-local": CalendarDays };
const WEEKDAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const parseDateKey = (value) => {
  const parsed = parseISO(String(value || ""));
  return isValid(parsed) ? startOfDay(parsed) : null;
};

const getInitialMonth = (value, min) => parseDateKey(value) || parseDateKey(min) || startOfDay(new Date());

export function TemporalField({ label, hint, type, className = "", ...inputProps }) {
  const Icon = ICONS[type] || CalendarDays;
  return (
    <label className="block min-w-0 max-w-full text-sm text-slate-700">
      <span className="mb-1 flex items-center gap-2 font-medium">
        <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
        {label}
      </span>
      {hint && <span className="mb-2 block text-xs text-slate-500">{hint}</span>}
      <input
        {...inputProps}
        type={type}
        className={`block h-11 w-full min-w-0 max-w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 disabled:bg-slate-100 disabled:text-slate-400 ${className}`}
      />
    </label>
  );
}

export function DateField({
  label,
  hint,
  className = "",
  visibleValue = "",
  visibleValueTestId,
  value = "",
  min,
  max,
  disabled = false,
  readOnly = false,
  onChange,
  name,
  required = false,
  "data-testid": testId,
  ...inputProps
}) {
  const [open, setOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(() => getInitialMonth(value, min));
  const selectedDate = useMemo(() => parseDateKey(value), [value]);
  const minDate = useMemo(() => parseDateKey(min), [min]);
  const maxDate = useMemo(() => parseDateKey(max), [max]);
  const today = startOfDay(new Date());
  const displayValue = visibleValue || formatDateInputValueIT(value);
  const monthDays = useMemo(() => eachDayOfInterval({
    start: startOfWeek(startOfMonth(visibleMonth), { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(visibleMonth), { weekStartsOn: 1 }),
  }), [visibleMonth]);
  const previousMonth = subMonths(visibleMonth, 1);
  const nextMonth = addMonths(visibleMonth, 1);
  const previousDisabled = Boolean(minDate && isBefore(endOfMonth(previousMonth), minDate));
  const nextDisabled = Boolean(maxDate && isBefore(maxDate, startOfMonth(nextMonth)));

  useEffect(() => {
    if (open) setVisibleMonth(getInitialMonth(value, min));
  }, [min, open, value]);

  const selectDate = (date) => {
    const dateKey = format(date, "yyyy-MM-dd");
    onChange?.({ target: { value: dateKey, name }, currentTarget: { value: dateKey, name } });
    setOpen(false);
  };

  return (
    <div className="block min-w-0 max-w-full text-sm text-slate-700">
      <span className="mb-1 block font-medium">{label}</span>
      {hint && <span className="mb-2 block text-xs text-slate-500">{hint}</span>}
      <button
        {...inputProps}
        type="button"
        value={value}
        disabled={disabled || readOnly}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-required={required || undefined}
        aria-label={`${label}: ${displayValue}`}
        className={`group grid min-h-12 w-full min-w-0 max-w-full cursor-pointer grid-cols-1 grid-rows-1 overflow-hidden rounded-sm border border-slate-300 bg-white shadow-sm transition-colors hover:border-slate-500 hover:bg-slate-50 active:bg-slate-100 focus-visible:border-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:bg-slate-100 ${className}`}
        data-testid={testId ? `${testId}-trigger` : undefined}
      >
        <span
          className="pointer-events-none col-start-1 row-start-1 flex min-h-12 w-full min-w-0 max-w-full items-center justify-center px-11 text-center text-sm font-semibold text-slate-900"
          data-testid={visibleValueTestId}
        >
          {displayValue}
        </span>
        <CalendarDays aria-hidden="true" className="pointer-events-none col-start-1 row-start-1 ml-4 h-5 w-5 self-center justify-self-start text-slate-600" />
        <ChevronDown aria-hidden="true" className="pointer-events-none col-start-1 row-start-1 mr-4 h-5 w-5 self-center justify-self-end text-slate-500 transition-transform group-aria-expanded:rotate-180" />
      </button>
      <input
        type="date"
        value={value}
        min={min}
        max={max}
        name={name}
        onChange={onChange}
        tabIndex={-1}
        aria-hidden="true"
        className="sr-only"
        data-testid={testId}
      />

      <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60" />
          <DialogPrimitive.Content
            className="fixed left-1/2 top-1/2 z-50 grid w-[calc(100%_-_2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 gap-4 rounded-lg border border-slate-200 bg-white p-4 shadow-xl focus:outline-none sm:p-5"
            data-testid={testId ? `${testId}-calendar` : "date-field-calendar"}
          >
          <DialogPrimitive.Title className="pr-8 text-left text-lg font-semibold text-slate-950">{label}</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Scegli un giorno dal calendario oppure chiudi senza modificare la data.
          </DialogPrimitive.Description>

          <div className="flex items-center justify-between gap-3" data-testid="date-calendar-navigation">
            <button
              type="button"
              onClick={() => setVisibleMonth(previousMonth)}
              disabled={previousDisabled}
              className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-sm border border-slate-300 bg-white text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Mese precedente"
              data-testid="date-calendar-previous-month"
            >
              <ChevronLeft aria-hidden="true" className="h-5 w-5" />
            </button>
            <p className="capitalize text-base font-semibold text-slate-950" aria-live="polite" data-testid="date-calendar-month-label">
              {format(visibleMonth, "MMMM yyyy", { locale: it })}
            </p>
            <button
              type="button"
              onClick={() => setVisibleMonth(nextMonth)}
              disabled={nextDisabled}
              className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-sm border border-slate-300 bg-white text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Mese successivo"
              data-testid="date-calendar-next-month"
            >
              <ChevronRight aria-hidden="true" className="h-5 w-5" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1" role="grid" aria-label={format(visibleMonth, "MMMM yyyy", { locale: it })}>
            {WEEKDAYS.map((weekday) => (
              <span key={weekday} className="py-1 text-center text-xs font-semibold text-slate-500" role="columnheader">{weekday}</span>
            ))}
            {monthDays.map((date) => {
              const dateKey = format(date, "yyyy-MM-dd");
              const isSelected = Boolean(selectedDate && isSameDay(date, selectedDate));
              const isToday = isSameDay(date, today);
              const isDisabled = Boolean((minDate && isBefore(date, minDate)) || (maxDate && isBefore(maxDate, date)));
              const outsideMonth = !isSameMonth(date, visibleMonth);
              return (
                <button
                  key={dateKey}
                  type="button"
                  onClick={() => selectDate(date)}
                  disabled={isDisabled}
                  aria-label={format(date, "EEEE d MMMM yyyy", { locale: it })}
                  aria-pressed={isSelected}
                  data-today={isToday ? "true" : undefined}
                  data-testid={`date-calendar-day-${dateKey}`}
                  className={`min-h-11 min-w-0 rounded-sm border text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 ${isSelected ? "border-slate-900 bg-slate-900 text-white" : isToday ? "border-emerald-600 bg-emerald-50 text-emerald-900" : "border-transparent bg-white text-slate-800 hover:border-slate-300 hover:bg-slate-50"} ${outsideMonth ? "opacity-50" : ""} disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 disabled:opacity-50`}
                >
                  {format(date, "d")}
                </button>
              );
            })}
          </div>

          <DialogPrimitive.Close asChild>
            <button type="button" className="min-h-11 w-full rounded-sm border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700" data-testid="date-calendar-cancel">
              Chiudi senza modificare
            </button>
          </DialogPrimitive.Close>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </div>
  );
}

export function formatDateInputValueIT(value) {
  const date = parseDateKey(value);
  if (!date) return "Scegli una data";
  return format(date, "d MMMM yyyy", { locale: it });
}

export const TimeField = (props) => <TemporalField {...props} type="time" />;
export const DateTimeField = (props) => <TemporalField {...props} type="datetime-local" />;
